import { WarningTwoTone } from "@ant-design/icons";
import { IntegrationConfig } from "@p0security/shared/integrations/shared";
import { Alert } from "antd";
import { useFlags } from "launchdarkly-react-client-sdk";
import React, { useMemo } from "react";

import { useFirestoreCollection } from "../../providers/FirestoreProvider";
import ContactSupport from "./components/ContactSupport";
import IntegrationTable from "./components/IntegrationTable";
import {
  approvers,
  directories,
  notifiers,
  resourceIntegrations,
  siems,
  trackers,
} from "./constants";
import "./styles.less";

export const IntegrationsList: React.FC<object> = () => {
  const flags = useFlags();

  const numberOfResourcesSupported = flags.cloudResourcesSupported;

  const { docs: installationConfigs } =
    useFirestoreCollection<IntegrationConfig>("integrations", {
      live: true,
      tenantAware: true,
    });
  const installations = useMemo(
    () =>
      Object.fromEntries(
        (installationConfigs ?? []).map((c) => [c.id, c.data])
      ),
    [installationConfigs]
  );

  const siemsData = useMemo(
    () =>
      siems.filter(
        (s) => s.key === "datadog" || flags.splunkHttpEventCollector
      ),
    [flags.splunkHttpEventCollector]
  );

  return (
    <>
      {numberOfResourcesSupported === 1 && (
        <Alert
          message={
            <>
              Your current plan only allows for the support of a single
              resource. To upgrade and enable support for multiple resources,
              please
              <ContactSupport />.
            </>
          }
          type="warning"
          icon={<WarningTwoTone twoToneColor="#faad14" />}
          showIcon
        />
      )}
      <IntegrationTable
        title="Notifiers"
        data={notifiers}
        installations={installations}
        zeroNotes={
          <>
            <WarningTwoTone />
            &nbsp;You must install a notifier to get real-time notifications.
          </>
        }
      />
      <IntegrationTable
        title="Trackers"
        data={trackers}
        installations={installations}
      />
      <IntegrationTable
        title="Security Information & Event Managers"
        data={siemsData}
        installations={installations}
      />
      <IntegrationTable
        title="Approvers"
        data={approvers}
        installations={installations}
      />
      <IntegrationTable
        title="Directories"
        data={directories}
        installations={installations}
      />
      <IntegrationTable
        title="Resources"
        data={resourceIntegrations(flags)}
        installations={installations}
        zeroNotes={
          <div className="additional-details">
            <WarningTwoTone />
            &nbsp;You must install at least one resource to use P0.
          </div>
        }
      />
    </>
  );
};
